import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import { protectedRoutes } from './protected';
import { publicRoutes } from './public';
import { VersionDashboard } from '@/features/versions/components/VersionDashboard';
import { CategorySplitView } from '@/features/categories/components/CategorySplitView';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { MainLayout } from '@/components/layout/MainLayout';
import { useParams } from 'react-router-dom';
import { VersionList } from '@/features/versions/components/VersionList';
import { useVersionStatus } from '@/hooks/useVersion';
import { MyAccount } from '@/features/account/components/MyAccount';
import { VersionDetails } from '@/features/versions/components/VersionDetails';
import { VersionPreviewPage } from "@/features/versions/components/preview/VersionPreviewPage";
import { BulkPlanAssignerPage } from '@/features/channels/pages/BulkPlanAssignerPage';
// Wrapper component to handle params
const CategorySplitViewWrapper = () => {
  const { providerId, versionId } = useParams();
  const { data: statusData } = useVersionStatus(Number(providerId), Number(versionId));

  return (
    <CategorySplitView 
      providerId={Number(providerId)} 
      versionId={Number(versionId)}
      isReadOnly={statusData?.isPublished ?? false}
    />
  );
};

// Wrapper component for CategoryForm
export const routes: RouteObject[] = [
  ...publicRoutes,
  {
    path: '/',
    element: (
      <ErrorBoundary>
        <MainLayout />
      </ErrorBoundary>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/providers" replace />
      },
      ...protectedRoutes,
      {
        path: 'providers/:providerId/versions',
        element: (
          <ErrorBoundary>
            <VersionList />
          </ErrorBoundary>
        )
      },
      {
        path: 'providers/:providerId/regions/:regionId/versions',
        element: (
          <ErrorBoundary>
            <VersionList />
          </ErrorBoundary>
        )
      },
      {
        path: 'providers/:providerId/versions/:versionId/dashboard',
        element: <VersionDashboard />
      },
      {
        path: 'providers/:providerId/regions/:regionId/versions/:versionId/dashboard',
        element: <VersionDashboard />
      },
      {
        path: 'providers/:providerId/versions/:versionId',
        element: <VersionDashboard />,
        children: [
          {
            path: 'categories',
            element: <CategorySplitViewWrapper />
          }
        ]
      },
      {
        path: 'providers/:providerId/regions/:regionId/versions/:versionId',
        element: <VersionDashboard />,
        children: [
          {
            path: 'categories',
            element: <CategorySplitViewWrapper />
          }
        ]
      },
      {
        path: 'account',
        element: (
          <ErrorBoundary>
            <MyAccount />
          </ErrorBoundary>
        )
      },
      {
        path: 'providers/:providerId/versions/:versionId/bulk-plan-assigner',
        element: (
          <ErrorBoundary>
            <BulkPlanAssignerPage />
          </ErrorBoundary>
        )
      },
      {
        path: 'providers/:providerId/regions/:regionId/versions/:versionId/bulk-plan-assigner',
        element: (
          <ErrorBoundary>
            <BulkPlanAssignerPage />
          </ErrorBoundary>
        )
      }
    ]
  },
  {
    path: '*',
    element: <Navigate to="/providers" replace />
  },
  {
    path: "/providers/:providerId/versions/:versionId",
    element: <VersionDetails />
  },
  {
    path: "/providers/:providerId/regions/:regionId/versions/:versionId",
    element: <VersionDetails />
  },
  {
    path: "/providers/:providerId/versions/:versionId/preview",
    element: <VersionPreviewPage />
  },
  {
    path: "/providers/:providerId/regions/:regionId/versions/:versionId/preview",
    element: <VersionPreviewPage />
  }
];

// Add feature flags to opt-in to new behavior and avoid warnings
export const router = createBrowserRouter(routes, {
  future: {
    v7_startTransition: true,
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_relativeSplatPath: true,
    v7_skipActionErrorRevalidation: true,
    v7_partialHydration: true
  } as any // Type assertion to avoid TypeScript errors with future flags
});